import React, { memo, useCallback, useMemo } from 'react';
import { Trans } from 'react-i18next';

import DataTable from '@common/components/datatable';
import type { DataTableColumn } from '@common/components/datatable/types';
import LinkWrapper from '@common/components/linkWrapper';
import ProfileImage from '@common/components/profile-image';
import mergeNames from '@common/utils/merged-names-text';
import type { SimpleUser, User } from '@common/types/objects';

import type { AudienceSearchModule } from './audience-user-search';
import {
  type AudienceUserSearchFilter,
  type AudienceUserSearchResponse,
  useAudienceUserSearch,
} from './use-audience-user-search';

type PredicatesAudienceUserRow = SimpleUser & Pick<User, 'networks' | 'functions'>;

interface PredicatesAudienceUsersTableProps {
  organisationId: string;
  filter: AudienceUserSearchFilter;
  baseUrl?: string;
  maxShownItems?: number;
  limit?: number;
  enabled?: boolean;
  allowOrganisationWideAudienceSearch?: boolean;
  audienceSearchModule?: AudienceSearchModule;
  placeholder?: React.ReactNode;
  emptyView?: React.ReactNode;
  onSearchResult?: (result: AudienceUserSearchResponse) => void;
  showViewAllFooter?: boolean;
  onViewAll?: () => void;
  /** Modal audience list — load more footer + scroll-to-fetch (not inline preview). */
  pagedLoadMore?: boolean;
  className?: string;
  maxHeight?: string | number;
}

function buildPredicatesAudienceColumns(
  baseUrl: string,
  maxShownItems: number,
): DataTableColumn[] {
  return [
    {
      key: 'profile',
      label: '',
      options: {
        size: 35,
        render: (row: PredicatesAudienceUserRow) => (
          <LinkWrapper url={`${baseUrl}/users/${row.id}/about`} target="_blank">
            <ProfileImage enablePopover size={35} user={row} />
          </LinkWrapper>
        ),
      },
    },
    {
      key: 'full_name',
      label: <Trans i18nKey="common:predicates_filter_column_name_label" />,
      className: 'Table__Cell__Title',
      options: {
        render: (row: PredicatesAudienceUserRow) => (
          <LinkWrapper url={`${baseUrl}/users/${row.id}/about`} target="_blank">
            <span className="fs-exclude">{row.full_name}</span>
          </LinkWrapper>
        ),
      },
    },
    {
      key: 'networks',
      label: <Trans i18nKey="common:predicates_filter_column_communities_label" />,
      options: {
        render: (row: PredicatesAudienceUserRow) => (
          // @ts-expect-error networks shape matches mergeNames usage in legacy PredicatesUserItem
          <span>{mergeNames(row.networks, maxShownItems, 'name', undefined, '+')}</span>
        ),
      },
    },
    {
      key: 'functions',
      label: <Trans i18nKey="common:predicates_filter_column_functions_label" />,
      options: {
        render: (row: PredicatesAudienceUserRow) => (
          // @ts-expect-error functions shape matches mergeNames usage in legacy PredicatesUserItem
          <span>{mergeNames(row.functions, maxShownItems, 'name', undefined, '+')}</span>
        ),
      },
    },
  ];
}

function PredicatesAudienceUsersTableComponent({
  organisationId,
  filter,
  baseUrl = '/admin',
  maxShownItems = 3,
  limit = 10,
  enabled = true,
  allowOrganisationWideAudienceSearch = true,
  audienceSearchModule,
  placeholder,
  emptyView,
  onSearchResult,
  showViewAllFooter = false,
  onViewAll,
  pagedLoadMore = false,
  className,
  maxHeight = 'auto',
}: PredicatesAudienceUsersTableProps) {
  const columns = useMemo(
    () => buildPredicatesAudienceColumns(baseUrl, maxShownItems),
    [baseUrl, maxShownItems]
  );

  const handleSearchResult = useCallback((result: AudienceUserSearchResponse) => {
    onSearchResult?.(result);
  }, [onSearchResult]);

  const {
    data,
    meta,
    loading,
    refetch,
  } = useAudienceUserSearch({
    organisationId,
    filter,
    limit,
    enabled,
    allowOrganisationWideAudienceSearch,
    audienceSearchModule,
    onSuccess: handleSearchResult,
  });

  const pagination = meta?.pagination;
  const totalCount = pagination?.total_count ?? 0;
  const hasMore = totalCount > data.length;

  const handleLoadMore = useCallback(async () => {
    if (loading || !hasMore) return;
    await refetch();
  }, [loading, hasMore, refetch]);

  const remainingCount = Math.max(
    0,
    totalCount - (pagination?.limit ?? limit) - (pagination?.offset ?? 0),
  );
  const resolvedEmptyView = emptyView ?? placeholder;
  const showTableLoadMore = pagedLoadMore && hasMore;

  return (
    <>
      <DataTable
        className={className}
        columns={columns}
        data={data as PredicatesAudienceUserRow[]}
        loading={loading && (data.length === 0 || pagedLoadMore)}
        maxHeight={pagedLoadMore ? 'auto' : maxHeight}
        stickyHeader={false}
        emptyView={resolvedEmptyView}
        pagination={
          showTableLoadMore
            ? {
              useOffset: true,
              offset: pagination?.offset ?? 0,
              limit: pagination?.limit ?? limit,
              total_count: totalCount,
              mode: 'loadmore',
            }
            : undefined
        }
        onShowMore={showTableLoadMore ? handleLoadMore : undefined}
      />
      {showViewAllFooter && !pagedLoadMore && remainingCount > 0 && (
        <div
          className="PredicatesFilter__ShowMore"
          onClick={onViewAll}
          role="button"
        >
          <Trans
            i18nKey="common:predicates_filter_view_all_employees"
            values={{ count: remainingCount }}
          />
        </div>
      )}
    </>
  );
}

export const PredicatesAudienceUsersTable = memo(PredicatesAudienceUsersTableComponent);
